import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import format from 'string-template';

import CloseIcon from '@mui/icons-material/Close';

import ChatContext from '../../context/ChatContext';
import ChatChannelContext from '../../context/ChatChannelContext';
import { Event } from '../../utils/events';
import ChatUserProfilePicture from '../ChatUserProfilePicture';

import {
  Container,
  Header,
  Title,
  Subtitle,
  CloseButton,
  Tabs,
  Tab,
  BulkAction,
  TruncationNotice,
  RowList,
  Row,
  RowMain,
  RowTextGroup,
  RowTitle,
  RowPreview,
  UnreadBadge,
  RowActions,
  RowAction,
  EmptyState,
  EmptyTitle,
} from './styles';
import texts from './texts.json';

const InboxTab = {
  UNREAD: 'UNREAD',
  RECENT: 'RECENT',
};

const TAB_ID = {
  [InboxTab.UNREAD]: 'threadInboxTabUnread',
  [InboxTab.RECENT]: 'threadInboxTabRecent',
};

const AVATAR_SIZE = 34;

/**
 * The coach's queue of threads for the open channel: browse it, jump into any of them, or dismiss
 * one without answering. Replaces the old pill behaviour of stepping through threads one at a time.
 *
 * Rows come from the replies the channel already has loaded — every reply carries its root's text
 * as `parent`, so listing twenty threads costs no extra reads.
 *
 * A row opens its thread on click: the queue exists to be jumped into, so that cannot cost two
 * clicks. `Mark read` sits beside it as the secondary action on rows that still carry unread
 * replies.
 */
const ThreadInbox = () => {
  const { logEvent, authUser } = useContext(ChatContext);
  const {
    activeChannel,
    unreadThreadRows,
    recentThreadRows,
    isUnreadTruncated,
    markThreadRead,
    markAllRead,
    setIsThreadInboxOpen,
    setThreadNoticeRootId,
  } = useContext(ChatChannelContext);

  const [activeTab, setActiveTab] = useState(InboxTab.UNREAD);
  const containerRef = useRef(null);

  const isUnreadTab = activeTab === InboxTab.UNREAD;
  const rows = isUnreadTab ? unreadThreadRows : recentThreadRows;

  const memberData = useMemo(() => activeChannel?.memberData || {}, [activeChannel]);

  // The drawer mounts when it opens, so this is the point where a keyboard lands in it.
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const eventContext = () => ({
    userId: authUser?.id,
    timestamp: new Date().toISOString(),
    channelId: activeChannel?.id,
  });

  const openThread = (rootId, position) => {
    logEvent(Event.THREAD_INBOX_THREAD_OPENED, {
      ...eventContext(),
      rootMessageId: rootId,
      // Which row they reached for: every open landing on the first row would mean the queue is
      // doing nothing the old pill did not already do.
      position,
      tab: activeTab,
    });
    setThreadNoticeRootId(rootId);
    setIsThreadInboxOpen(false);
  };

  const markRead = (rootId, newestUnseenAt, unreadCount) => {
    logEvent(Event.THREAD_INBOX_MARKED_READ, {
      ...eventContext(),
      rootMessageId: rootId,
      unreadCount,
    });
    markThreadRead(rootId, newestUnseenAt);
  };

  const renderRow = (row, index) => {
    const {
      rootId,
      rootText,
      rootUserId,
      latestReply,
      newestUnseenAt,
      unreadCount,
    } = row;
    const member = memberData[rootUserId] || {};

    return (
      <Row key={rootId}>
        <RowMain
          type="button"
          onClick={() => openThread(rootId, index)}
        >
          <ChatUserProfilePicture
            image={member.image || ''}
            name={member.name || ''}
            size={AVATAR_SIZE}
          />
          <RowTextGroup>
            <RowTitle>{rootText || texts.noPreview}</RowTitle>
            <RowPreview>{latestReply?.text || texts.noPreview}</RowPreview>
          </RowTextGroup>
          {unreadCount > 0 && (
            <UnreadBadge>{format(texts.newReplies, { count: unreadCount })}</UnreadBadge>
          )}
        </RowMain>
        {unreadCount > 0 && (
          <RowActions>
            {/*
              Marks up to the newest reply the coach has *not* seen. Their own trailing messages were
              never unread, so carrying the row's latest reply instead would move the watermark past
              nothing.
            */}
            <RowAction
              type="button"
              onClick={() => markRead(rootId, newestUnseenAt, unreadCount)}
            >
              {texts.markRead}
            </RowAction>
          </RowActions>
        )}
      </Row>
    );
  };

  return (
    <Container
      ref={containerRef}
      tabIndex={-1}
      role="region"
      aria-label={texts.title}
    >
      <Header>
        <div>
          <Title>{texts.title}</Title>
          <Subtitle>{texts.subtitle}</Subtitle>
        </div>
        <CloseButton
          type="button"
          aria-label={texts.close}
          onClick={() => setIsThreadInboxOpen(false)}
        >
          <CloseIcon fontSize="small" />
        </CloseButton>
      </Header>
      <Tabs role="tablist">
        <Tab
          type="button"
          id={TAB_ID[InboxTab.UNREAD]}
          role="tab"
          aria-selected={isUnreadTab}
          $active={isUnreadTab}
          onClick={() => setActiveTab(InboxTab.UNREAD)}
        >
          {`${texts.unreadTab} ${unreadThreadRows.length}`}
        </Tab>
        <Tab
          type="button"
          id={TAB_ID[InboxTab.RECENT]}
          role="tab"
          aria-selected={!isUnreadTab}
          $active={!isUnreadTab}
          onClick={() => setActiveTab(InboxTab.RECENT)}
        >
          {texts.recentTab}
        </Tab>
      </Tabs>
      {/*
        markAllRead() derives its watermark from the Recent window, and the unread and Recent queries
        are independent unsynchronized fetches — either can fail silently and leave its array empty.
        Requiring Recent non-empty keeps the button from rendering live but doing nothing when pressed.

        Truncation counts on its own, without a row to point at: the flag counts raw replies while
        the rows exclude the viewer's own, so a window full of the coach's own replies leaves no rows
        while older unread ones sit below the seatbelt. That is exactly when the bulk clear is the
        only way out of the backlog, so it has to stay reachable.
      */}
      {isUnreadTab && (unreadThreadRows.length > 0 || isUnreadTruncated) && recentThreadRows.length > 0 && (
        <BulkAction
          type="button"
          onClick={() => {
            logEvent(Event.THREAD_INBOX_MARKED_ALL_READ, {
              ...eventContext(),
              unreadThreadCount: unreadThreadRows.length,
            });
            markAllRead();
          }}
        >
          {texts.markAllRead}
        </BulkAction>
      )}
      {/* The seatbelt bound the query, so unread threads exist below this list. Saying so is what
          keeps the omission from being silent again — the failure this whole change exists to remove.
          Shown with no rows too: that case is not "nothing to report", it is the whole backlog
          sitting below the seatbelt with the empty state claiming otherwise. */}
      {isUnreadTab && isUnreadTruncated && (
        <TruncationNotice>{texts.truncatedNotice}</TruncationNotice>
      )}
      {rows.length === 0
        ? (
          <EmptyState
            role="tabpanel"
            aria-labelledby={TAB_ID[activeTab]}
          >
            <EmptyTitle>{isUnreadTab ? texts.emptyUnread : texts.emptyRecent}</EmptyTitle>
            <div>{isUnreadTab ? texts.emptyUnreadHint : texts.emptyRecentHint}</div>
          </EmptyState>
        )
        : (
          <RowList
            role="tabpanel"
            aria-labelledby={TAB_ID[activeTab]}
          >
            {rows.map(renderRow)}
          </RowList>
        )}
    </Container>
  );
};

export default observer(ThreadInbox);
